import React, {
  useEffect,
  useState
} from 'react';

import {
  get,
  post,
  del
} from '../utils/api';

const decodePublicKey = value => {
  const padding =
    '='.repeat(
      (4 - value.length % 4) % 4
    );

  const base64 =
    (value + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

  const decoded =
    window.atob(base64);

  return Uint8Array.from(
    decoded,
    character =>
      character.charCodeAt(0)
  );
};

const deviceName = () => {
  const agent =
    navigator.userAgent || '';

  if (/iPhone|iPad/i.test(agent)) {
    return 'iPhone or iPad';
  }

  if (/Android/i.test(agent)) {
    return 'Android device';
  }

  if (/Windows/i.test(agent)) {
    return 'Windows computer';
  }

  if (/Macintosh/i.test(agent)) {
    return 'Mac computer';
  }

  return 'Browser device';
};

export default function DevicePushControl() {
  const [supported, setSupported] =
    useState(true);

  const [enabled, setEnabled] =
    useState(false);

  const [activeDevices, setActiveDevices] =
    useState(0);

  const [saving, setSaving] =
    useState(false);

  const [message, setMessage] =
    useState('');

  const [permission, setPermission] =
    useState(
      typeof Notification ===
        'undefined'
        ? 'unsupported'
        : Notification.permission
    );

  const isAppleMobile =
    /iPhone|iPad/i.test(
      navigator.userAgent || ''
    );

  const isStandalone =
    window.matchMedia?.(
      '(display-mode: standalone)'
    )?.matches ||
    window.navigator.standalone === true;

  const loadStatus = async () => {
    const registration =
      await navigator.serviceWorker.getRegistration('/');
    const subscription = registration
      ? await registration.pushManager.getSubscription()
      : null;

    const [accountResponse, deviceResponse] = await Promise.all([
      get('/notifications/push/status'),
      subscription
        ? post('/notifications/push/device-status', {
            endpoint: subscription.endpoint
          })
        : Promise.resolve({ data: { enabled: false } })
    ]);

    setActiveDevices(
      Number(accountResponse.data?.activeDevices || 0)
    );
    setPermission(Notification.permission);
    setEnabled(
      Notification.permission === 'granted' &&
      deviceResponse.data?.enabled === true
    );
  };

  useEffect(() => {
    const browserSupported =
      window.isSecureContext &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;

    setSupported(browserSupported);

    if (browserSupported) {
      loadStatus().catch(() => {});
    }
  }, []);

  const getRegistration = async () => {
    await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none'
    });

    let timeoutId;
    try {
      return await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((resolve, reject) => {
          timeoutId = window.setTimeout(
            () => reject(new Error(
              'Device alerts could not start. Refresh and try again.'
            )),
            15000
          );
        })
      ]);
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const enablePush = async () => {
    if (saving) {
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      if (
        isAppleMobile &&
        !isStandalone
      ) {
        throw new Error(
          'On iPhone or iPad, add BloansBooks to your Home Screen before enabling device alerts.'
        );
      }

      const permissionResult =
        await Notification
          .requestPermission();

      setPermission(permissionResult);

      if (
        permissionResult !== 'granted'
      ) {
        throw new Error(
          permissionResult === 'denied'
            ? 'Notifications are blocked in this browser. Allow them in the site settings.'
            : 'Notification permission was not granted.'
        );
      }

      const keyResponse =
        await get(
          '/notifications/push/public-key'
        );

      const publicKey =
        keyResponse.data?.publicKey;

      if (
        !keyResponse.data?.configured ||
        !publicKey
      ) {
        throw new Error(
          'Device notifications are not available right now.'
        );
      }

      const registration =
        await getRegistration();

      let subscription =
        await registration.pushManager
          .getSubscription();

      if (!subscription) {
        subscription =
          await registration.pushManager
            .subscribe({
              userVisibleOnly:true,
              applicationServerKey:
                decodePublicKey(
                  publicKey
                )
            });
      }

      await post(
        '/notifications/push/subscribe',
        {
          subscription:
            subscription.toJSON(),
          device_label:deviceName()
        }
      );

      await loadStatus();

      setMessage(
        'Device alerts enabled on this device.'
      );
    } catch (error) {
      setMessage(
        error.message ||
        'Device alerts could not be enabled.'
      );
    } finally {
      setSaving(false);
    }
  };

  const disablePush = async () => {
    if (saving) {
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      const registration =
        await navigator.serviceWorker
          .getRegistration('/');

      const subscription =
        registration
          ? await registration
              .pushManager
              .getSubscription()
          : null;

      if (subscription) {
        await del(
          '/notifications/push/unsubscribe',
          {
            endpoint:
              subscription.endpoint
          }
        );

        await subscription.unsubscribe();
      }

      await loadStatus();

      setEnabled(false);
      setMessage(
        'Device alerts disabled on this device.'
      );
    } catch (error) {
      setMessage(
        error.message ||
        'Device alerts could not be disabled.'
      );
    } finally {
      setSaving(false);
    }
  };

  const denied =
    permission === 'denied';

  return (
    <div
      style={{
        padding:'10px 16px',
        background:'#F8F5EE',
        borderBottom:
          '1px solid #E7E0D5'
      }}
    >
      <div
        style={{
          display:'flex',
          alignItems:'center',
          justifyContent:
            'space-between',
          gap:12
        }}
      >
        <div>
          <div
            style={{
              fontSize:12,
              fontWeight:600,
              color:'#3B2A21'
            }}
          >
            Device notification alerts
          </div>

          <div
            style={{
              fontSize:10,
              color:'#8A8A8A',
              marginTop:2
            }}
          >
            Important and critical alerts
            on your notification bar
          </div>

          {activeDevices > 0 && (
            <div
              style={{
                fontSize:9,
                color:'#556B2F',
                marginTop:3
              }}
            >
              {activeDevices}{' '}
              active device
              {activeDevices === 1
                ? ''
                : 's'}
            </div>
          )}
        </div>

        <button
          type="button"
          disabled={
            saving ||
            !supported ||
            denied
          }
          onClick={
            enabled
              ? disablePush
              : enablePush
          }
          style={{
            border:
              enabled
                ? '1px solid #556B2F'
                : '1px solid #B9B1A5',
            background:
              enabled
                ? '#556B2F'
                : '#fff',
            color:
              enabled
                ? '#fff'
                : '#3B2A21',
            borderRadius:14,
            padding:'5px 10px',
            fontSize:10,
            fontWeight:700,
            cursor:
              saving
                ? 'wait'
                : 'pointer',
            opacity:
              !supported || denied
                ? 0.55
                : 1
          }}
        >
          {saving
            ? 'Working...'
            : enabled
              ? 'Device alerts on'
              : denied
                ? 'Blocked'
                : supported
                  ? 'Enable alerts'
                  : 'Unavailable'}
        </button>
      </div>

      {message && (
        <div
          style={{
            marginTop:7,
            fontSize:10,
            lineHeight:1.4,
            color:
              enabled
                ? '#556B2F'
                : '#8F2E24'
          }}
        >
          {message}
        </div>
      )}

      {isAppleMobile &&
       !isStandalone &&
       !message && (
        <div
          style={{
            marginTop:7,
            fontSize:10,
            lineHeight:1.4,
            color:'#8B5E0A'
          }}
        >
          On iPhone or iPad, first add
          BloansBooks to your Home Screen.
        </div>
      )}
    </div>
  );
}
